/**
 * InfluencerMarketing.tsx
 *
 * File purpose:
 * - Influencer Marketing service page.
 * - Loads influencer dataset from src/data/influencers.json using a robust loader:
 *   dynamic import -> fetch fallback -> graceful empty fallback.
 * - Integrates SEO component and maintains the existing UI unchanged.
 *
 * Notes:
 * - This file intentionally avoids embedding the full dataset inline to keep code maintainable.
 * - The influencer grid is now sorted by engagement rate (descending) so highest engagement appears first.
 */

import React, { useMemo, useState, useEffect } from 'react'
import { Link } from 'react-router'
import Seo from '../../components/Seo'
import { Button } from '../../components/ui/button'
import {
  Users,
  Heart,
  TrendingUp,
  Target,
  CheckCircle,
  Filter,
  ExternalLink
} from 'lucide-react'
import ManualUpdateModal from '../../components/influencer/ManualUpdateModal'
import BulkImportExport from '../../components/influencer/BulkImportExport'
import { useInfluencerData } from '../../hooks/useInfluencerData'

/**
 * Influencer data model (tier is computed, not stored)
 */
interface Influencer {
  /** Display name */
  name: string
  /** Audience region: 'Kurd' | 'Arab' */
  audience: 'Kurd' | 'Arab'
  /** Followers (absolute number) */
  followers: number
  /** Engagement rate as percentage (e.g. 3.5 for 3.5%) */
  engagementRate: number
  /** Instagram profile URL */
  instagramLink: string
  /** Optional profile picture URL (can be empty) */
  profilePicture?: string
}

/**
 * Utility: format followers into K/M for UI
 * @param count - numeric follower count
 * @returns formatted string like "1.2M" or "34K"
 */
const formatFollowers = (count: number): string => {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${Math.round(count / 1_000)}K`
  return `${count}`
}

/**
 * Utility: compute tier from followers
 *
 * Tier definitions:
 * 1. Nano  — 1,000 – 10,000 followers
 * 2. Micro — 10,000 – 100,000 followers
 * 3. Macro — 100,000 – 1,000,000 followers
 * 4. Mega  — 1,000,000+ followers
 */
type Tier = 'Nano' | 'Micro' | 'Macro' | 'Mega'
const getTierFromFollowers = (followers: number): Tier => {
  if (followers >= 1_000 && followers < 10_000) return 'Nano'
  if (followers >= 10_000 && followers < 100_000) return 'Micro'
  if (followers >= 100_000 && followers < 1_000_000) return 'Macro'
  return 'Mega'
}

/**
 * Normalize an Instagram link or username into a safe full URL.
 * @param url - Raw instagram link or username
 * @returns normalized instagram URL string
 */
const normalizeLink = (url: string | undefined): string => {
  if (!url) return 'https://www.instagram.com/'
  let cleaned = url.trim().replace(/\s+/g, '')
  cleaned = cleaned.replace(/^@+/, '')
  if (/^https?:\/\//i.test(cleaned)) {
    return cleaned
  }
  if (/instagram\.com/i.test(cleaned)) {
    cleaned = cleaned.replace(/^\//, '')
    return `https://${cleaned}`
  }
  if (cleaned.includes('/')) {
    return `https://${cleaned}`
  }
  const encoded = encodeURIComponent(cleaned)
  return `https://www.instagram.com/${encoded}`
}

/**
 * Attempt to load the influencers JSON using multiple strategies:
 * 1) Dynamic import (preferred for bundlers that support JSON import)
 * 2) Fetch from a few likely public paths (fallback)
 * 3) If all fail, return an empty array.
 *
 * @returns Promise<Influencer[]>
 */
async function loadInfluencersJson(): Promise<Influencer[]> {
  // 1) Try dynamic import
  try {
    // dynamic import often works in modern bundlers; the module may have .default
    const mod = await import('../../data/influencers.json' as any)
    // module default or module itself (some bundlers)
    const data = (mod && (mod.default ?? mod)) as Influencer[] | undefined
    if (Array.isArray(data)) return data
  } catch (err) {
    // ignore and try fetch fallbacks
    // console.info('Dynamic import of influencers.json failed, trying fetch fallback.')
  }

  // 2) Try fetch with a list of plausible public paths.
  const candidates = [
    '/src/data/influencers.json',
    '/data/influencers.json',
    '/influencers.json',
    // attempt relative URL based on current location
    `${window.location.origin}/src/data/influencers.json`
  ]

  for (const path of candidates) {
    try {
      const res = await fetch(path, { cache: 'no-cache' })
      if (!res.ok) continue
      const json = (await res.json()) as Influencer[] | undefined
      if (Array.isArray(json)) return json
    } catch {
      // ignore and continue to next candidate
    }
  }

  // 3) Final fallback: empty dataset
  console.warn('Could not load influencers.json via import or fetch. Falling back to empty list.')
  return []
}

/**
 * InfluencerMarketingPage component
 *
 * Responsible for:
 * - Loading influencer dataset robustly
 * - Providing the same UI and filters as before
 * - Sorting influencers by engagement rate (descending) for display
 */
export default function InfluencerMarketingPage() {
  const [selectedRegion, setSelectedRegion] = useState<'All' | 'Kurdish' | 'Arab'>('All')
  const [selectedTier, setSelectedTier] = useState<'All' | Tier>('All')
  const [loadingData, setLoadingData] = useState<boolean>(true)

  // Use custom hook for data management. Start with empty array and import later.
  const { influencers, lastSync, updateInfluencer, importInfluencers, resetToInitial } =
    useInfluencerData([])

  /**
   * Load influencers from JSON on mount.
   * - Tries dynamic import first then fetch fallbacks.
   * - On success calls importInfluencers to populate the hook/store.
   */
  useEffect(() => {
    let mounted = true
    setLoadingData(true)

    const run = async () => {
      try {
        const data = await loadInfluencersJson()
        if (!mounted) return
        if (data.length > 0) {
          importInfluencers(data)
        } else {
          // if empty, keep existing state (or optionally reset)
          // resetToInitial() // optionally reset if you want to clear persisted storage
        }
      } catch (err) {
        console.warn('Error while loading influencer data', err)
      } finally {
        if (mounted) setLoadingData(false)
      }
    }

    run()

    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Get influencers filtered by selected region and tier (computed)
   * and sorted by engagementRate in descending order (highest engagement first).
   */
  const filteredInfluencers = useMemo(() => {
    const filtered = influencers.filter((inf) => {
      const regionMatch =
        selectedRegion === 'All' || (selectedRegion === 'Kurdish' ? inf.audience === 'Kurd' : inf.audience === 'Arab')
      const tier = getTierFromFollowers(inf.followers)
      const tierMatch = selectedTier === 'All' || tier === selectedTier
      return regionMatch && tierMatch
    })

    // Sort by engagementRate descending so highest engagement appears first
    return filtered.slice().sort((a, b) => b.engagementRate - a.engagementRate)
  }, [influencers, selectedRegion, selectedTier])

  /**
   * Compute tier counts after region filtering
   */
  const tierCounts = useMemo(() => {
    const base = influencers.filter((inf) => selectedRegion === 'All' || (selectedRegion === 'Kurdish' ? inf.audience === 'Kurd' : inf.audience === 'Arab'))
    return {
      nano: base.filter((i) => getTierFromFollowers(i.followers) === 'Nano').length,
      micro: base.filter((i) => getTierFromFollowers(i.followers) === 'Micro').length,
      macro: base.filter((i) => getTierFromFollowers(i.followers) === 'Macro').length,
      mega: base.filter((i) => getTierFromFollowers(i.followers) === 'Mega').length,
    }
  }, [influencers, selectedRegion])

  /**
   * Page-specific FAQ structured data (JSON-LD)
   * - Helps Google AI Overviews / other AI systems provide clear answers and reference the page.
   */
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What regions does Edicoo serve for influencer marketing?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Edicoo serves Kurdish and Arabic markets across Iraq, with dedicated local teams in Erbil and regional coverage for Baghdad and Kurdistan.'
        }
      },
      {
        '@type': 'Question',
        name: 'How do you select influencers for a campaign?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We select influencers based on audience relevance, verified engagement rates, historical performance and alignment with campaign KPIs. We provide sample reach and engagement forecasts with every proposal.'
        }
      },
      {
        '@type': 'Question',
        name: 'Can you provide performance case studies?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes — our portfolio contains detailed case studies with campaign objectives, approach and measured KPIs. Contact us to request relevant case examples for your industry.'
        }
      }
    ]
  }

  return (
    <div>
      {/* SEO: non-visual head updates and structured data */}
      <Seo
        title="Influencer Marketing — Edicoo"
        description="Connect authentically with Kurdish and Arabic audiences. Edicoo provides influencer selection, campaign management and measurable analytics for Iraq markets."
        url="https://your-site.com/services/influencer-marketing"
        image="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/15c08900-9eb7-4dd0-b764-47bc928699b3.jpg"
        jsonLd={[faqJsonLd]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-900 via-orange-800 to-rose-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">Influencer Marketing</h1>
              <p className="text-xl text-orange-100 leading-relaxed mb-8">
                Connect authentically with your target audience through strategic partnerships with key influencers who align with your brand values and objectives.
              </p>
              <Button asChild size="lg" className="bg-white text-orange-700 hover:bg-orange-50">
                <Link to="/contact">Start Your Campaign</Link>
              </Button>
            </div>
            <div>
              <img
                src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/15c08900-9eb7-4dd0-b764-47bc928699b3.jpg"
                alt="Influencer Marketing"
                className="w-full h-80 object-cover rounded-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Influencer Marketing Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategic approach to influencer partnerships that deliver authentic engagement and measurable results
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-4">
              Partnered with more than 150 influencers for over 500 pieces of content in recent years.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Strategy & Planning</h3>
              <p className="text-gray-600">Define goals, target audience, and campaign objectives</p>
            </div>

            <div className="text-center">
              <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Influencer Selection</h3>
              <p className="text-gray-600">Identify and vet influencers that align with your brand</p>
            </div>

            <div className="text-center">
              <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Campaign Execution</h3>
              <p className="text-gray-600">Manage partnerships and content creation process</p>
            </div>

            <div className="text-center">
              <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Analysis & Optimization</h3>
              <p className="text-gray-600">Track performance and optimize for better results</p>
            </div>
          </div>
        </div>
      </section>

      {/* Influencer Database with Filters */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Partnered Influencers' Network</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Access to over {influencers.length} verified influencers across Kurdish and Arabic markets
            </p>
          </div>

          {/* Filter Controls */}
          <div className="bg-white p-6 rounded-2xl shadow-lg mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Filter className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Filter Influencers</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Region Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Region</label>
                <div className="flex gap-2">
                  {['All', 'Kurdish', 'Arab'].map((region) => (
                    <button
                      key={region}
                      onClick={() => setSelectedRegion(region as any)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedRegion === region
                          ? 'bg-orange-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {region}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tier Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Influencer Tier</label>
                <div className="flex gap-2 flex-wrap">
                  {(['All', 'Nano', 'Micro', 'Macro', 'Mega'] as const).map((tier) => (
                    <button
                      key={tier}
                      onClick={() => setSelectedTier(tier as any)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedTier === tier
                          ? 'bg-orange-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tier}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Tier Definitions - clickable and linked to influencer grid */}
            <div className="mt-6 p-4 bg-orange-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Tier Definitions:</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <button
                  onClick={() => {
                    setSelectedTier('Nano')
                    const el = document.getElementById('influencer-grid')
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow focus:outline-none"
                >
                  <div className="w-3 h-3 bg-green-500 rounded-full" />
                  <div>
                    <div className="font-semibold">Nano</div>
                    <div className="text-xs text-gray-600">1,000 – 10,000 followers ({tierCounts.nano})</div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setSelectedTier('Micro')
                    const el = document.getElementById('influencer-grid')
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow focus:outline-none"
                >
                  <div className="w-3 h-3 bg-blue-500 rounded-full" />
                  <div>
                    <div className="font-semibold">Micro</div>
                    <div className="text-xs text-gray-600">10,000 – 100,000 followers ({tierCounts.micro})</div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setSelectedTier('Macro')
                    const el = document.getElementById('influencer-grid')
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow focus:outline-none"
                >
                  <div className="w-3 h-3 bg-purple-500 rounded-full" />
                  <div>
                    <div className="font-semibold">Macro</div>
                    <div className="text-xs text-gray-600">100,000 – 1,000,000 followers ({tierCounts.macro})</div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setSelectedTier('Mega')
                    const el = document.getElementById('influencer-grid')
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow focus:outline-none"
                >
                  <div className="w-3 h-3 bg-red-500 rounded-full" />
                  <div>
                    <div className="font-semibold">Mega</div>
                    <div className="text-xs text-gray-600">1,000,000+ followers ({tierCounts.mega})</div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Results Summary */}
          <div className="mb-6">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-orange-600">{filteredInfluencers.length}</span> influencers
              {selectedRegion !== 'All' && ` in ${selectedRegion} market`}
              {selectedTier !== 'All' && ` in ${selectedTier} tier`}
              {loadingData && <span className="ml-2 text-sm text-gray-500">Loading data...</span>}
            </p>
          </div>

          {/* Influencer Grid */}
          <div id="influencer-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredInfluencers.map((influencer, index) => {
              const computedTier = getTierFromFollowers(influencer.followers)
              return (
                <div key={`${influencer.name}-${index}`} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                  <div className="p-6">
                    {/* Profile Picture and Basic Info */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex-shrink-0">
                        <img
                          src={influencer.profilePicture || `https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/8c62b5f6-89cd-43c1-bc80-52c6a955e31b.jpg`}
                          alt={influencer.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                          onError={(e) => {
                            e.currentTarget.src = `https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/8c62b5f6-89cd-43c1-bc80-52c6a955e31b.jpg`
                          }}
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{influencer.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            influencer.audience === 'Kurd' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {influencer.audience === 'Kurd' ? 'Kurdish' : 'Arab'}
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            computedTier === 'Nano' ? 'bg-green-100 text-green-700' :
                            computedTier === 'Micro' ? 'bg-blue-100 text-blue-700' :
                            computedTier === 'Macro' ? 'bg-purple-100 text-purple-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {computedTier}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 text-sm">Followers:</span>
                        <span className="font-semibold text-gray-900">{formatFollowers(influencer.followers)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 text-sm">Engagement:</span>
                        <span className="font-semibold text-orange-600">{influencer.engagementRate}%</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <a
                        href={normalizeLink(influencer.instagramLink)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 flex-1 justify-center px-4 py-2 bg-gradient-to-r from-orange-600 to-rose-600 text-white rounded-lg hover:from-orange-700 hover:to-rose-700 transition-colors"
                      >
                        <span>View Profile</span>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {filteredInfluencers.length === 0 && !loadingData && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No influencers found matching your filters.</p>
              <button
                onClick={() => {
                  setSelectedRegion('All')
                  setSelectedTier('All')
                }}
                className="mt-4 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">What We Offer</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-orange-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Macro & Micro Influencer Partnerships</h3>
                    <p className="text-gray-600">From celebrity endorsements to niche micro-influencers</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-orange-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Regional Expertise</h3>
                    <p className="text-gray-600">Deep understanding of Kurdish and Arabic markets</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-orange-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Campaign Management</h3>
                    <p className="text-gray-600">End-to-end campaign execution and influencer relationship management</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-orange-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Performance Analytics</h3>
                    <p className="text-gray-600">Detailed reporting on reach, engagement, and ROI metrics</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/512dc89b-3d94-4f7e-bef6-93d175317cd5.jpg"
                alt="Social Media Analytics"
                className="w-full h-96 object-cover rounded-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-900 to-rose-800 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Ready to Amplify Your Brand?</h2>
          <p className="text-xl text-orange-100 mb-8">
            Let us connect you with the right influencers from our network of {influencers.length}+ verified creators
          </p>
          <Button asChild size="lg" className="bg-white text-orange-700 hover:bg-orange-50 text-lg px-8">
            <Link to="/contact">Start Your Influencer Campaign</Link>
          </Button>
        </div>
      </section>
    </div>
  )
}